import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormGroup } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import {
  EmperorCardHeaderColor,
  EmperorDialogConfig,
  EmperorDialogRef,
  EmperorDialogSize,
  EmperorUIButtonTypes,
} from '@boldpenguin/emperor-presentational';
import { AppointmentTypes } from '@boldpenguin/sdk';
import { select, Store } from '@ngrx/store';
import { filter, map, merge, Observable, switchMap, tap } from 'rxjs';
import {
  IEnrollment,
  IEnrollmentAttribute,
  IEnrollmentAttributes,
} from 'src/app/core/models/enrollments.model';
import { CarrierMessageService } from 'src/app/core/services/carrier-message-service/carrier-message.service';
import {
  MixpanelEventType,
  MixpanelService,
} from 'src/app/core/services/mixpanel-service/mixpanel.service';
import { EnrollmentDialogActions } from 'src/app/core/store/enrollments/enrollments.actions';
import {
  selectEnrollmentIsSaving,
  selectEnrollmentServerErrorsById,
} from 'src/app/core/store/enrollments/enrollments.selectors';
import { SettingsSubFeatures } from 'src/app/features/settings/models/sub-features';
import {
  AppointmentAction,
  dialogConfigs,
  IAppointmentDialogData,
  IDialogConfig,
  resourceGuideKeys,
} from '../../models/appointment-dialog.model';
import { CarrierMessage } from '../../models/carrier-messages';
import { IFormControlMetadata } from '../../models/forms.model';
import {
  buildDirectAppointmentForm,
  toHumanReadable,
} from '../../utilities/direct-appointment-utilities';

@Component({
  selector: 'app-appointment-dialog',
  templateUrl: './appointment-dialog.component.html',
  styleUrls: ['./appointment-dialog.component.scss'],
})
export class AppointmentDialogComponent implements OnInit {
  serverErrorMessages$: Observable<{
    errors: string[] | undefined;
    hasErrors: boolean;
  }>;
  isSaving$: Observable<boolean>;
  data: IAppointmentDialogData;
  form: UntypedFormGroup;
  formControlMetadata: IFormControlMetadata[] = [];
  logErrorEvent: MixpanelEventType;
  carrierAlerts: CarrierMessage[];

  readonly EmperorUIButtonTypes = EmperorUIButtonTypes;
  readonly EmperorDialogSize = EmperorDialogSize;
  readonly EmperorCardHeaderColor = EmperorCardHeaderColor;
  readonly AppointmentAction = AppointmentAction;
  readonly resourceGuideKeys = resourceGuideKeys;

  constructor(
    private store: Store,
    private carrierMessageService: CarrierMessageService,
    private dialogRef: EmperorDialogRef,
    private mixpanelService: MixpanelService,
    private destroyRef: DestroyRef,
    private router: Router,
    config: EmperorDialogConfig,
  ) {
    this.data = config.data;
  }

  ngOnInit(): void {
    this.isSaving$ = this.store.pipe(select(selectEnrollmentIsSaving));

    if (this.data.isDirect) {
      this.form = buildDirectAppointmentForm(this.data.enrollment);
      this.formControlMetadata = this.getFormControlMetadata(
        this.data.enrollment.enrollment_attributes,
      );
    }

    this.logErrorEvent = this.data.isFTW
      ? MixpanelEventType.WELCOME_DIRECT_APPOINTMENT_DIALOG_LOGO_ERROR
      : MixpanelEventType.APPOINTMENT_DIALOG_LOGO_ERROR;

    this.serverErrorMessages$ = this.store.pipe(
      select(selectEnrollmentServerErrorsById(this.data.enrollment.id)),
    );

    this.subscribeDialogClose();

    this.carrierAlerts =
      this.carrierMessageService.getSubAppointmentMessagesForCarrier(
        this.data.enrollment.carrier_subdomain,
      );
  }

  get dialogConfig(): IDialogConfig {
    switch (this.data.type) {
      case AppointmentAction.View:
        return dialogConfigs.view;
      case AppointmentAction.Pending:
        return dialogConfigs.pending;
      case AppointmentAction.Edit:
        return dialogConfigs.editDirect;
      case AppointmentAction.Disconnect:
        return dialogConfigs.disconnect;
      case AppointmentAction.Connect:
        return this.data.isDirect
          ? dialogConfigs.connectDirect
          : this.hasCarrierQuestions
            ? dialogConfigs.carrierQuestionSet
            : dialogConfigs.connectSub;
    }
  }

  get hasCarrierQuestions() {
    return this.data.incompleteQuestionGroups?.includes(
      this.data.enrollment.carrier_subdomain,
    );
  }

  isViewState() {
    return (
      this.data.type === AppointmentAction.View ||
      this.data.type === AppointmentAction.Pending
    );
  }

  onSubmitClick(enrollment: IEnrollment): void {
    this.form?.markAsPristine();
    if (
      this.data.type === AppointmentAction.Connect ||
      this.data.type === AppointmentAction.Edit ||
      this.data.type === AppointmentAction.Disconnect
    ) {
      this.trackMixpanelDialogAction();
      this.updateAppointment(
        enrollment,
        this.data.type !== AppointmentAction.Disconnect,
      );
    } else {
      this.trackMixpanelDialogAction();
      this.dialogRef.close();
    }
  }

  trackMessages(i: number, carrierMessage: CarrierMessage): string {
    return carrierMessage.message;
  }

  showCancel(): boolean {
    return !this.isViewState();
  }

  cancel() {
    this.trackMixpanelDialogAction(true);
    this.dialogRef.close();
  }

  private subscribeDialogClose(): void {
    const enrollmentSavedWithoutError$ = this.store.pipe(
      // when saving an enrollment...
      select(selectEnrollmentIsSaving),
      filter(Boolean),
      switchMap(() => this.serverErrorMessages$),
      tap((messages) => {
        if (messages.hasErrors) {
          this.mixpanelService.track(
            MixpanelEventType.APPOINTMENT_FE_SERVER_ERROR,
            { message: messages.errors ?? [] },
          );
        }
      }),
      // and successful saved without errors (fix errors in dialog)...
      filter((messages) => !messages.hasErrors),
    );

    // or redirected to compliance questions...
    const navigationToComplianceForm$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event) =>
        (event as NavigationEnd).urlAfterRedirects.includes(
          SettingsSubFeatures.COMPLIANCE_FORM,
        ),
      ),
      filter(Boolean),
    );

    merge(enrollmentSavedWithoutError$, navigationToComplianceForm$)
      .pipe(takeUntilDestroyed(this.destroyRef))
      // ... close the dialog.
      .subscribe(() => this.dialogRef.close());
  }

  private trackMixpanelDialogAction(isCancel = false) {
    this.mixpanelService.track(MixpanelEventType.APPOINTMENT_DIALOG_ACTION, {
      dialogType: this.data.type,
      isCancel,
      carrier: this.data.enrollment.carrier_subdomain,
      appointmentType: this.data.isDirect
        ? AppointmentTypes.Direct
        : AppointmentTypes.Sub,
    });
  }

  private updateAppointment(
    enrollment: IEnrollment,
    shouldActivate: boolean,
  ): void {
    if (this.data.isFTW) {
      if (this.data.isDirect) {
        if (this.data.removeQueuedEnrollment) {
          this.store.dispatch(
            EnrollmentDialogActions.removeQueuedAppointment({
              id: enrollment.id,
            }),
          );
        } else {
          const carrierAttributes = { ...this.form?.value };
          if (this.data.type === AppointmentAction.Disconnect) {
            for (const attr in carrierAttributes) {
              if (!!carrierAttributes[attr]) carrierAttributes[attr] = '';
            }
          }
          this.store.dispatch(
            EnrollmentDialogActions.preconfigureAppointment({
              enrollment: {
                ...enrollment,
                carrier_attributes: carrierAttributes,
              },
              successMessage: this.dialogConfig.successMessage,
            }),
          );
        }
      }
    } else {
      if (shouldActivate) {
        if (this.data.isDirect) {
          this.store.dispatch(
            EnrollmentDialogActions.editDirectAppointment({
              enrollment: {
                ...enrollment,
                carrier_attributes: this.form?.value,
              },
              successMessage: this.dialogConfig.successMessage,
            }),
          );
        } else {
          this.store.dispatch(
            EnrollmentDialogActions.requestSubAppointment({
              enrollment,
              successMessage: this.dialogConfig.successMessage,
            }),
          );
        }
      } else {
        this.store.dispatch(
          EnrollmentDialogActions.deactivateAppointment({
            enrollment,
            successMessage: this.dialogConfig.successMessage,
          }),
        );
      }
    }
  }

  private getFormControlMetadata(
    enrollmentAttributes: IEnrollmentAttributes,
  ): IFormControlMetadata[] {
    return Object.keys(enrollmentAttributes).map((key) => ({
      formControlName: key,
      label: toHumanReadable(key),
      hint:
        this.data.type !== AppointmentAction.View &&
        this.data.type !== AppointmentAction.Disconnect &&
        enrollmentAttributes[key].help_text,
      errorText: enrollmentAttributes[key].error_text,
      inputType: this.isPasswordInput(key, enrollmentAttributes[key])
        ? 'password'
        : 'text',
    }));
  }

  private isPasswordInput(
    key: string,
    enrollmentAttributes: IEnrollmentAttribute,
  ): boolean {
    return Object.values(enrollmentAttributes)
      .map(String)
      .concat(key)
      .some((value: string) => value.toLowerCase().includes('password'));
  }
}
